import { useLocation, useHistory } from "react-router-dom";

import Box from "@/components/Box";
import Circle from "@/components/Circle";
import { ProductFiltersDesktop } from "@/components/ProductFiltersSidebar";
import { theme } from "@/core/theme";
import { EVENT_CONNECTIONS_QUERY } from "@/graphql/knowledgeCenter/webinars";
import useWindowSize from "@/hooks/use-window-size";
import { useDebounce } from "@/hooks/useDebounce";
import useListControls from "@/hooks/useListControls";
import CalendarIcon from "@/images/CalendarIcon";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import { useQuery } from "@apollo/client";

import EventCard from "./EventCard";
import LearningHubListSidebar from "./LearningHubListSidebar";
import LearningHubListToolbar from "./LearningHubListToolbar";
import { ListTemplateInfiniteScrollObjectList } from "./ListTemplate";
import {
  dashboardMainLayoutPb,
  dashboardMainLayoutPt,
  dashboardMainLayoutPx,
  dashboardMainStackMaxWidth,
  dashboardPageMaxWidth,
  dashboardSidebarContentFlexDirection,
  dashboardShopListControlsToContentGap,
  dashboardShopListSubtitleControlsGap,
  dashboardSidebarContentGap
} from "./dashboardLayout";
import useEventFilters from "./useEventFilters";

export function EventsTable({ children }) {
  const { width } = useWindowSize();
  const isMobile = width < 768;

  if (isMobile) {
    return (
      <Stack gap={20} mt={40}>
        {children}
      </Stack>
    );
  }

  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: "0px 20px"
      }}
    >
      <Box as="thead">
        <Box
          as="tr"
          fontFamily="gilroyBold"
          fontSize={12}
          letterSpacing={2.8}
          textTransform="uppercase"
        >
          <Box
            pt={2}
            pb={20}
            pl={40}
            fontFamily="inherit"
            fontSize="inherit"
            letterSpacing="inherit"
            textTransform="inherit"
            as="th"
            style={{
              width: "50%"
            }}
            textAlign="left"
          >
            event
          </Box>
          <Box
            pt={2}
            pb={20}
            as="th"
            px={20}
            textAlign="left"
            fontFamily="inherit"
            fontSize="inherit"
            letterSpacing="inherit"
            textTransform="inherit"
            minWidth={200}
          >
            time & date
          </Box>
          <Box
            pt={2}
            pb={20}
            as="th"
            fontFamily="inherit"
            fontSize="inherit"
            letterSpacing="inherit"
            textTransform="inherit"
          >
            details
          </Box>
        </Box>
      </Box>
      <tbody>{children}</tbody>
    </table>
  );
}

export default function Events() {
  const location = useLocation();
  const history = useHistory();
  const search = new URLSearchParams(location.search).get("search") || "";
  const debouncedSearch = useDebounce(search, 200);

  const { toggleFilter, resetFilters, allFilters, validatedFilters, types, getFilterLabel } =
    useEventFilters();

  const controls = useListControls({
    sortOptions: [
      {
        label: "Title asc",
        value: "title"
      },
      {
        label: "Title desc",
        value: "-title"
      },
      {
        label: "Start time asc",
        value: "start_time"
      },
      {
        label: "Start time desc",
        value: "-start_time"
      }
    ]
  });

  const { data, loading, fetchMore } = useQuery(EVENT_CONNECTIONS_QUERY, {
    variables: {
      first: 10,
      orderBy: controls.sort,
      search: debouncedSearch,
      types
    }
  });

  const hasMore = data?.eventConnections.pageInfo.hasNextPage;
  const endCursor = data?.eventConnections.pageInfo.endCursor;
  const events = data?.eventConnections.edges.map(edge => edge.node);

  const filterDesktop = (
    <ProductFiltersDesktop
      allFilters={allFilters}
      validatedFilters={validatedFilters}
      toggleFilter={toggleFilter}
      onReset={resetFilters}
      getFilterLabel={getFilterLabel}
    />
  );

  return (
    <Group
      flexDirection={dashboardSidebarContentFlexDirection}
      justifyContent="center"
      pt={dashboardMainLayoutPt}
      pb={dashboardMainLayoutPb}
      px={dashboardMainLayoutPx}
      maxWidth={dashboardPageMaxWidth}
      mx="auto"
      gap={dashboardSidebarContentGap}
    >
      <LearningHubListSidebar filterDesktop={filterDesktop} />
      <Stack
        gap={[...dashboardShopListSubtitleControlsGap.slice(0, 2), 80]}
        flexGrow={1}
        width="100%"
        minWidth={0}
        maxWidth={dashboardMainStackMaxWidth}
      >
        <Stack gap={20}>
          <Group gap={20} alignItems="center">
            <Circle bg="transparent" size={80} border="2px solid black">
              <CalendarIcon fill={theme.colors.dark} size={40} />
            </Circle>
            <Box fontFamily="gilroyBold" fontSize={[36, 36, 68]}>
              Upcoming events
            </Box>
          </Group>
        </Stack>
        <Stack gap={0} width="100%" minWidth={0}>
          <Stack gap={20} minWidth={0} width="100%">
            <LearningHubListToolbar
              allFilters={allFilters}
              validatedFilters={validatedFilters}
              toggleFilter={toggleFilter}
              resetFilters={resetFilters}
              getFilterLabel={getFilterLabel}
              search={search}
              onSearchChange={value => {
                const searchParams = new URLSearchParams(location.search);
                searchParams.set("search", value);
                history.push({
                  path: location.path,
                  search: searchParams.toString()
                });
              }}
              sortControls={controls}
            />
          </Stack>
          <Stack mt={dashboardShopListControlsToContentGap} gap={0} minWidth={0} width="100%">
            <ListTemplateInfiniteScrollObjectList
              Container={EventsTable}
              containerProps={{
                pb: theme.spacing.section.pb
              }}
              loading={loading}
              objects={events}
              objectsPlural="events"
              hasMore={hasMore}
              fetchMore={fetchMore}
              endCursor={endCursor}
              Card={EventCard}
            />
          </Stack>
        </Stack>
      </Stack>
    </Group>
  );
}
